/**
 * useOutreachCounts — shared, polled badge count for the outreach nav (§9).
 *
 * One module-level poller feeds every consumer (sidebar "Outreach" item, the
 * hub tab strip) so the badge stays in sync without each surface fetching
 * independently. The count comes from an endpoint that already exists:
 *   - needsAction → GET /outreach/notifications (NotificationSummary)
 *
 * Degrades silently: returns 0 until loaded, and keeps the last good value if a
 * refresh fails. Pass `enabled: false` (e.g. the user lacks outreach.view) to
 * opt a consumer out entirely.
 */

import { useEffect, useState } from 'react';
import { registerOrgScopedReset } from '@/store/orgScopedReset';
import { inboxApi, opsApi } from './services/outreach.api';

export type OutreachCounts = {
    needsAction: number;
    /** The engine tick or inbound drain heartbeat has gone stale (H11). */
    opsStale: boolean;
    /** Last engine heartbeat (ISO) — null when never recorded. */
    engineLastTickAt: string | null;
    /** Connected sending accounts currently down (disconnected/error) — Stage 1.
     *  Drives the persistent "reconnect an account" banner in the hub. */
    accountsDown: number;
};

const EMPTY: OutreachCounts = { needsAction: 0, opsStale: false, engineLastTickAt: null, accountsDown: 0 };
const POLL_MS = 60_000;

let current: OutreachCounts = EMPTY;
const subscribers = new Set<(counts: OutreachCounts) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;

async function refresh() {
    if (inFlight) return;
    inFlight = true;
    try {
        const [notif, ops] = await Promise.allSettled([inboxApi.notifications(), opsApi.health()]);
        const needsAction = notif.status === 'fulfilled' ? notif.value.data.needsAction ?? 0 : current.needsAction;
        const accountsDown =
            notif.status === 'fulfilled' ? notif.value.data.accountsDown ?? 0 : current.accountsDown;
        const opsStale = ops.status === 'fulfilled' ? !ops.value.data.healthy : current.opsStale;
        const engineLastTickAt =
            ops.status === 'fulfilled' ? ops.value.data.engine.lastTickAt : current.engineLastTickAt;
        const next: OutreachCounts = { needsAction, opsStale, engineLastTickAt, accountsDown };
        if (
            next.needsAction !== current.needsAction ||
            next.opsStale !== current.opsStale ||
            next.engineLastTickAt !== current.engineLastTickAt ||
            next.accountsDown !== current.accountsDown
        ) {
            current = next;
            subscribers.forEach((fn) => fn(current));
        }
    } finally {
        inFlight = false;
    }
}

function ensurePolling() {
    if (timer) return;
    void refresh();
    timer = setInterval(() => void refresh(), POLL_MS);
}

function maybeStopPolling() {
    if (subscribers.size === 0 && timer) {
        clearInterval(timer);
        timer = null;
    }
}

/** Subscribe to the shared outreach badge counts. */
export function useOutreachCounts(enabled = true): OutreachCounts {
    const [counts, setCounts] = useState<OutreachCounts>(current);

    useEffect(() => {
        if (!enabled) return undefined;
        subscribers.add(setCounts);
        setCounts(current);
        ensurePolling();
        return () => {
            subscribers.delete(setCounts);
            maybeStopPolling();
        };
    }, [enabled]);

    return enabled ? counts : EMPTY;
}

/** Imperatively re-pull after the user acts (e.g. sends a reply). */
export function refreshOutreachCounts() {
    void refresh();
}

// Org switch (no page reload): the module-level cache would keep showing the
// PREVIOUS org's badge counts. Clear it, notify subscribers, and — if anyone is
// actively subscribed — immediately re-pull for the new organization.
registerOrgScopedReset(() => {
    current = EMPTY;
    subscribers.forEach((fn) => fn(current));
    if (timer) void refresh();
});
